import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// React 18 StrictMode compatibility fix for react-beautiful-dnd
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

// Available icons mapping
const availableIcons = [
  {
    name: 'building',
    label: 'Building',
    image: '/images/dealCard/building.svg',
  },
  { name: 'users', label: 'Users', image: '/images/dealCard/users.svg' },
  { name: 'mail', label: 'Mail', image: '/images/dealCard/mail.svg' },
  { name: 'clock', label: 'Clock', image: '/images/dealCard/clock.svg' },
  { name: 'pin', label: 'Pin', image: '/images/dealCard/pin.svg' },
];

const DealCardEditor = ({
  dealCardConfig,
  flexFields,
  updateDealCardConfig,
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [iconMenuAnchor, setIconMenuAnchor] = useState(null);
  const [selectedFieldForIcon, setSelectedFieldForIcon] = useState(null);
  const [spacingMenuAnchor, setSpacingMenuAnchor] = useState(null);
  const [selectedFieldForSpacing, setSelectedFieldForSpacing] = useState(null);
  const [spacingInputValue, setSpacingInputValue] = useState('5');
  const [customLabelMenuAnchor, setCustomLabelMenuAnchor] = useState(null);
  const [selectedFieldForCustomLabel, setSelectedFieldForCustomLabel] =
    useState(null);
  const [customLabelInput, setCustomLabelInput] = useState('');
  const [fontMenuAnchor, setFontMenuAnchor] = useState(null);
  const [selectedFieldForFont, setSelectedFieldForFont] = useState(null);

  // Save function with status tracking
  const handleSave = async (newConfig) => {
    setIsSaving(true);

    try {
      await updateDealCardConfig(newConfig);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving deal card config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getDefaultDealCardConfig = () => ({
    layout: {
      rows: [
        {
          id: 'row-1',
          columns: [
            {
              id: 'col-1',
              width: '75%',
              fields: [
                { field: 'name', enabled: true, position: 0, showLabel: false },
              ],
            },
            {
              id: 'col-2',
              width: '20%',
              fields: [
                {
                  field: 'priority',
                  enabled: false,
                  position: 0,
                  showLabel: false,
                },
              ],
            },
          ],
        },
        {
          id: 'row-2',
          columns: [
            {
              id: 'col-1',
              width: '100%',
              fields: [
                {
                  field: 'description',
                  enabled: true,
                  position: 0,
                  showLabel: false,
                },
              ],
            },
          ],
        },
      ],
    },
    dropdown: {
      enabled: true,
      showEdit: true,
      showChangeStage: true,
      showSchedule: true,
      showDelete: true,
    },
    mapButtons: {
      enabled: true,
      showDirections: true,
      showOnMap: true,
    },
  });

  // Initialize config properly
  const [config, setConfig] = useState(() => {
    if (dealCardConfig && dealCardConfig.layout) {
      return dealCardConfig;
    }
    return getDefaultDealCardConfig();
  });

  const [availableFields, setAvailableFields] = useState([]);

  // Update config when dealCardConfig prop changes
  useEffect(() => {
    if (dealCardConfig && dealCardConfig.layout) {
      setConfig(dealCardConfig);
    }
  }, [dealCardConfig]);

  useEffect(() => {
    // Get all available flex fields that aren't already assigned
    const assignedFields = new Set();

    // Handle both old section-based and new row-based structures
    if (config.layout.rows) {
      // New row-based structure
      config.layout.rows.forEach((row) => {
        row.columns.forEach((column) => {
          column.fields.forEach((field) => {
            if (field.enabled) {
              assignedFields.add(field.field);
            }
          });
        });
      });
    } else {
      // Legacy section-based structure (for backward compatibility)
      Object.values(config.layout).forEach((section) => {
        Object.values(section).forEach((field) => {
          if (field.enabled) {
            assignedFields.add(field.field);
          }
        });
      });
    }

    const available = flexFields.filter(
      (field) => !assignedFields.has(field) && !isSpacingField(field)
    );
    setAvailableFields(available);
  }, [config, flexFields]);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const { source, destination, draggableId } = result;
    const newConfig = { ...config };

    // Parse droppable IDs (format: "row-X-col-Y" or "available")
    const parseDroppableId = (id) => {
      if (id === 'available') return { type: 'available' };
      const parts = id.split('-');
      if (parts.length === 4 && parts[0] === 'row' && parts[2] === 'col') {
        return {
          type: 'column',
          rowIndex: parseInt(parts[1], 10),
          colIndex: parseInt(parts[3], 10),
        };
      }
      return null;
    };

    const sourceInfo = parseDroppableId(source.droppableId);
    const destInfo = parseDroppableId(destination.droppableId);

    if (!sourceInfo || !destInfo) return;

    // Handle moving from available fields to a column
    if (sourceInfo.type === 'available' && destInfo.type === 'column') {
      const targetColumn =
        newConfig.layout.rows[destInfo.rowIndex].columns[destInfo.colIndex];

      // Insert field at the specified position
      const newField = {
        field: draggableId,
        enabled: true,
        position: destination.index,
        showLabel: false, // Default to hiding labels (consistent with mobile)
        icon: undefined, // No icon by default
      };

      // Update positions of existing fields
      targetColumn.fields.forEach((field) => {
        if (field.position >= destination.index) {
          field.position += 1;
        }
      });

      targetColumn.fields.push(newField);

      // Normalize positions to ensure they're sequential
      normalizeFieldPositions(targetColumn);

      setConfig(newConfig);
      setHasUnsavedChanges(true);
      return;
    }

    // Handle moving from a column back to available fields
    if (sourceInfo.type === 'column' && destInfo.type === 'available') {
      const sourceColumn =
        newConfig.layout.rows[sourceInfo.rowIndex].columns[sourceInfo.colIndex];

      // Remove field from source column
      sourceColumn.fields = sourceColumn.fields.filter(
        (field) => field.field !== draggableId
      );

      // Normalize positions to ensure they're sequential
      normalizeFieldPositions(sourceColumn);

      setConfig(newConfig);
      setHasUnsavedChanges(true);
      return;
    }

    // Handle moving between columns
    if (sourceInfo.type === 'column' && destInfo.type === 'column') {
      const sourceColumn =
        newConfig.layout.rows[sourceInfo.rowIndex].columns[sourceInfo.colIndex];
      const destColumn =
        newConfig.layout.rows[destInfo.rowIndex].columns[destInfo.colIndex];

      // Find and remove the field from source
      const fieldToMove = sourceColumn.fields.find(
        (field) => field.field === draggableId
      );
      if (!fieldToMove) return;

      sourceColumn.fields = sourceColumn.fields.filter(
        (field) => field.field !== draggableId
      );

      // Normalize positions in source column
      normalizeFieldPositions(sourceColumn);

      if (
        sourceInfo.rowIndex === destInfo.rowIndex &&
        sourceInfo.colIndex === destInfo.colIndex
      ) {
        // Reordering within the same column
        destColumn.fields.splice(destination.index, 0, fieldToMove);
      } else {
        // Moving to a different column
        destColumn.fields.splice(destination.index, 0, fieldToMove);
      }

      // Normalize positions in destination column (skip sorting since array is already in correct order)
      normalizeFieldPositions(destColumn, true);

      setConfig(newConfig);
      setHasUnsavedChanges(true);
    }
  };

  const toggleFieldVisibility = (rowIndex, colIndex, fieldName) => {
    const newConfig = { ...config };
    const column = newConfig.layout.rows[rowIndex].columns[colIndex];
    const field = column.fields.find((f) => f.field === fieldName);
    if (field) {
      field.enabled = !field.enabled;
      setConfig(newConfig);
      setHasUnsavedChanges(true);
    }
  };

  const toggleFieldLabel = (rowIndex, colIndex, fieldName) => {
    const newConfig = { ...config };
    const column = newConfig.layout.rows[rowIndex].columns[colIndex];
    const field = column.fields.find((f) => f.field === fieldName);
    if (field) {
      field.showLabel = !field.showLabel;
      setConfig(newConfig);
      setHasUnsavedChanges(true);
    }
  };

  const removeField = (rowIndex, colIndex, fieldName) => {
    const newConfig = { ...config };
    const column = newConfig.layout.rows[rowIndex].columns[colIndex];

    // Find the actual index of the field by field name
    const actualFieldIndex = column.fields.findIndex(
      (field) => field.field === fieldName
    );

    if (actualFieldIndex === -1) return; // Field not found

    // Remove the field from the column
    column.fields.splice(actualFieldIndex, 1);

    // Reorder remaining fields
    column.fields.forEach((field, index) => {
      field.position = index;
    });

    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const handleIconMenuOpen = (event, rowIndex, colIndex, fieldName) => {
    setIconMenuAnchor(event.currentTarget);
    setSelectedFieldForIcon({ rowIndex, colIndex, fieldName });
  };

  const handleIconMenuClose = () => {
    setIconMenuAnchor(null);
    setSelectedFieldForIcon(null);
  };

  const handleIconSelect = (iconName) => {
    if (selectedFieldForIcon) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForIcon;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        field.icon = iconName;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleIconMenuClose();
  };

  const removeIcon = () => {
    if (selectedFieldForIcon) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForIcon;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        delete field.icon;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleIconMenuClose();
  };

  // Spacing configuration functions
  const handleSpacingMenuOpen = (event, rowIndex, colIndex, fieldName) => {
    setSpacingMenuAnchor(event.currentTarget);
    setSelectedFieldForSpacing({ rowIndex, colIndex, fieldName });
  };

  const handleSpacingMenuClose = () => {
    setSpacingMenuAnchor(null);
    setSelectedFieldForSpacing(null);
  };

  const handleSpacingSelect = (spacingValue) => {
    if (selectedFieldForSpacing) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForSpacing;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        field.spacing = spacingValue;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleSpacingMenuClose();
  };

  const removeSpacing = () => {
    if (selectedFieldForSpacing) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForSpacing;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        delete field.spacing;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleSpacingMenuClose();
  };

  // Dropdown configuration functions
  const toggleDropdown = () => {
    const newConfig = { ...config };
    if (!newConfig.dropdown) {
      newConfig.dropdown = {
        enabled: true,
        showEdit: true,
        showChangeStage: true,
        showSchedule: true,
        showDelete: true,
      };
    } else {
      newConfig.dropdown.enabled = !newConfig.dropdown.enabled;
    }
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const toggleDropdownOption = (option) => {
    const newConfig = { ...config };
    if (!newConfig.dropdown) {
      newConfig.dropdown = {
        enabled: true,
        showEdit: true,
        showChangeStage: true,
        showSchedule: true,
        showDelete: true,
      };
    }
    newConfig.dropdown[option] = !newConfig.dropdown[option];
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  // Map buttons configuration functions
  const toggleMapButtons = () => {
    const newConfig = { ...config };
    if (!newConfig.mapButtons) {
      newConfig.mapButtons = {
        enabled: true,
        showDirections: true,
        showOnMap: true,
      };
    } else {
      newConfig.mapButtons.enabled = !newConfig.mapButtons.enabled;
    }
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const toggleMapButtonOption = (option) => {
    const newConfig = { ...config };
    if (!newConfig.mapButtons) {
      newConfig.mapButtons = {
        enabled: true,
        showDirections: true,
        showOnMap: true,
      };
    }
    newConfig.mapButtons[option] = !newConfig.mapButtons[option];
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  // Avatar configuration functions
  const toggleFieldAvatar = (rowIndex, colIndex, fieldName) => {
    const newConfig = { ...config };
    const column = newConfig.layout.rows[rowIndex].columns[colIndex];
    const field = column.fields.find((f) => f.field === fieldName);
    if (field) {
      field.showAvatar = !field.showAvatar;
      setConfig(newConfig);
      setHasUnsavedChanges(true);
    }
  };

  // Custom label configuration functions
  const handleCustomLabelMenuOpen = (event, rowIndex, colIndex, fieldName) => {
    const field = config.layout.rows[rowIndex].columns[colIndex].fields.find(
      (f) => f.field === fieldName
    );
    setCustomLabelInput(field?.customLabel || '');
    setCustomLabelMenuAnchor(event.currentTarget);
    setSelectedFieldForCustomLabel({ rowIndex, colIndex, fieldName });
  };

  const handleCustomLabelMenuClose = () => {
    setCustomLabelMenuAnchor(null);
    setSelectedFieldForCustomLabel(null);
    setCustomLabelInput('');
  };

  const handleCustomLabelSave = () => {
    if (selectedFieldForCustomLabel) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForCustomLabel;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        if (customLabelInput.trim()) {
          field.customLabel = customLabelInput.trim();
        } else {
          delete field.customLabel;
        }
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleCustomLabelMenuClose();
  };

  const removeCustomLabel = () => {
    if (selectedFieldForCustomLabel) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForCustomLabel;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        delete field.customLabel;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleCustomLabelMenuClose();
  };

  // Font configuration functions
  const handleFontMenuOpen = (event, rowIndex, colIndex, fieldName) => {
    setFontMenuAnchor(event.currentTarget);
    setSelectedFieldForFont({ rowIndex, colIndex, fieldName });
  };

  const handleFontMenuClose = () => {
    setFontMenuAnchor(null);
    setSelectedFieldForFont(null);
  };

  const updateFieldFont = (size) => {
    if (selectedFieldForFont) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForFont;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        if (!field.font) {
          field.font = {};
        }
        field.font.size = size;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
  };

  const resetFieldFont = () => {
    if (selectedFieldForFont) {
      const { rowIndex, colIndex, fieldName } = selectedFieldForFont;
      const newConfig = { ...config };
      const column = newConfig.layout.rows[rowIndex].columns[colIndex];
      const field = column.fields.find((f) => f.field === fieldName);
      if (field) {
        delete field.font;
        setConfig(newConfig);
        setHasUnsavedChanges(true);
      }
    }
    handleFontMenuClose();
  };

  // Handle adding spacing field
  const handleAddSpacing = () => {
    const spacingValue = parseInt(spacingInputValue) || 5;
    const spacingId = `spacing-${Date.now()}`;

    // Add to the first row, first column if available
    const newConfig = { ...config };
    if (!newConfig.layout.rows || newConfig.layout.rows.length === 0) {
      // Create a new row if none exist
      newConfig.layout.rows = [
        {
          id: 'row-1',
          columns: [
            {
              id: 'col-1',
              width: '100%',
              fields: [
                {
                  field: spacingId,
                  enabled: true,
                  position: 0,
                  spacing: spacingValue,
                },
              ],
            },
          ],
        },
      ];
    } else {
      // Add to the first column of the first row
      const firstRow = newConfig.layout.rows[0];
      const firstColumn = firstRow.columns[0];
      firstColumn.fields.push({
        field: spacingId,
        enabled: true,
        position: firstColumn.fields.length,
        spacing: spacingValue,
      });

      // Normalize positions to ensure they're sequential
      normalizeFieldPositions(firstColumn);
    }

    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  // Helper function to normalize field positions within a column
  const normalizeFieldPositions = (column, skipSorting = false) => {
    // Only sort if not skipping (for cases where array is already in correct order)
    if (!skipSorting) {
      column.fields.sort((a, b) => a.position - b.position);
    }

    // Assign sequential positions based on current array order
    column.fields.forEach((field, index) => {
      field.position = index;
    });
  };

  // Check if field is a spacing field
  const isSpacingField = (fieldName) => {
    return fieldName.startsWith('spacing-');
  };

  // Check if field supports avatars (setter/closer fields)
  const supportsAvatar = (fieldName) => {
    return ['setter', 'closer'].includes(fieldName);
  };

  // Get spacing value for spacing fields
  const getSpacingValue = (fieldName, customSpacing) => {
    if (customSpacing) return customSpacing;

    // For new dynamic spacing fields, return default
    if (fieldName.startsWith('spacing-')) {
      return 5; // Default spacing
    }

    // Legacy spacing fields (if any)
    switch (fieldName) {
      case 'spacing-small':
        return 5;
      case 'spacing-medium':
        return 10;
      case 'spacing-large':
        return 20;
      case 'spacing-custom':
        return 15;
      default:
        return 5;
    }
  };

  const addRow = () => {
    const newConfig = { ...config };

    // Initialize rows array if it doesn't exist (for legacy configs)
    if (!newConfig.layout.rows) {
      newConfig.layout.rows = [];
    }

    const newRowId = `row-${newConfig.layout.rows.length}`;

    // Start new rows with 2 columns by default (more useful than 1)
    newConfig.layout.rows.push({
      id: newRowId,
      columns: [
        {
          id: 'col-0',
          width: '50%',
          fields: [],
        },
        {
          id: 'col-1',
          width: '50%',
          fields: [],
        },
      ],
    });

    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const removeRow = (rowIndex) => {
    const newConfig = { ...config };

    // Ensure rows array exists
    if (!newConfig.layout.rows) return;

    newConfig.layout.rows.splice(rowIndex, 1);
    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const addColumn = (rowIndex) => {
    const newConfig = { ...config };

    // Ensure rows array exists
    if (!newConfig.layout.rows || !newConfig.layout.rows[rowIndex]) return;

    const row = newConfig.layout.rows[rowIndex];

    if (row.columns.length >= 3) return; // Max 3 columns

    const newColId = `col-${row.columns.length}`;

    // Adjust existing column widths
    if (row.columns.length === 1) {
      row.columns[0].width = '50%';
      row.columns.push({
        id: newColId,
        width: '50%',
        fields: [],
      });
    } else if (row.columns.length === 2) {
      row.columns[0].width = '33.33%';
      row.columns[1].width = '33.33%';
      row.columns.push({
        id: newColId,
        width: '33.33%',
        fields: [],
      });
    }

    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  const removeColumn = (rowIndex, colIndex) => {
    const newConfig = { ...config };

    // Ensure rows array exists
    if (!newConfig.layout.rows || !newConfig.layout.rows[rowIndex]) return;

    const row = newConfig.layout.rows[rowIndex];

    if (row.columns.length <= 1) return; // Must have at least 1 column

    // Move fields from removed column back to available
    row.columns.splice(colIndex, 1);

    // Adjust remaining column widths
    if (row.columns.length === 1) {
      row.columns[0].width = '100%';
    } else if (row.columns.length === 2) {
      row.columns[0].width = '50%';
      row.columns[1].width = '50%';
    }

    setConfig(newConfig);
    setHasUnsavedChanges(true);
  };

  // const updateColumnWidth = (rowIndex, colIndex, width) => {
  //   const newConfig = { ...config };
  //   newConfig.layout.rows[rowIndex].columns[colIndex].width = width;
  //   setConfig(newConfig);
  //   setHasUnsavedChanges(true);
  // };

  const renderColumn = (rowIndex, colIndex, column) => (
    <Box key={`${rowIndex}-${colIndex}`} sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Column {colIndex + 1} ({column.width})
        </Typography>
        <Box>
          <Button
            size="small"
            onClick={() => removeColumn(rowIndex, colIndex)}
            disabled={config.layout.rows[rowIndex].columns.length <= 1}
            sx={{ minWidth: 'auto', p: 0.5, mr: 0.5 }}
          >
            ✕
          </Button>
        </Box>
      </Box>

      <StrictModeDroppable droppableId={`row-${rowIndex}-col-${colIndex}`}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minHeight: 80,
              p: 1,
              border: snapshot.isDraggingOver
                ? '2px dashed #4caf50'
                : '2px dashed #e0e0e0',
              borderRadius: 1,
              backgroundColor: snapshot.isDraggingOver ? '#e8f5e8' : '#fafafa',
              transition: 'all 0.2s ease',
            }}
          >
            {column.fields
              .sort((a, b) => a.position - b.position)
              .map((field, fieldIndex) => (
                <Draggable
                  key={field.field}
                  draggableId={field.field}
                  index={fieldIndex}
                >
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 1,
                        p: 1,
                        backgroundColor: snapshot.isDragging
                          ? '#e3f2fd'
                          : '#f9f9f9',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box {...provided.dragHandleProps} sx={{ mr: 1 }}>
                        <DragIndicatorIcon color="action" />
                      </Box>
                      <Chip
                        label={
                          isSpacingField(field.field)
                            ? `Spacing (${getSpacingValue(
                                field.field,
                                field.spacing
                              )}px)`
                            : field.field
                        }
                        size="small"
                        sx={{ mr: 1, flex: 1 }}
                        color={field.enabled ? 'primary' : 'default'}
                      />
                      <Button
                        size="small"
                        onClick={() =>
                          toggleFieldVisibility(rowIndex, colIndex, field.field)
                        }
                        startIcon={
                          field.enabled ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )
                        }
                        sx={{ mr: 1 }}
                      >
                        {field.enabled ? 'Visible' : 'Hidden'}
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          toggleFieldLabel(rowIndex, colIndex, field.field)
                        }
                        startIcon={
                          field.showLabel !== false ? (
                            <LabelIcon />
                          ) : (
                            <LabelOffIcon />
                          )
                        }
                        color={
                          field.showLabel !== false ? 'primary' : 'inherit'
                        }
                        sx={{ mr: 1 }}
                      >
                        Label
                      </Button>
                      {!isSpacingField(field.field) && (
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleIconMenuOpen(
                              event,
                              rowIndex,
                              colIndex,
                              field.field
                            )
                          }
                          sx={{ mr: 1 }}
                          title={
                            field.icon ? `Icon: ${field.icon}` : 'Add Icon'
                          }
                        >
                          {field.icon ? (
                            <img
                              src={
                                availableIcons.find(
                                  (icon) => icon.name === field.icon
                                )?.image
                              }
                              alt={field.icon}
                              style={{ width: 16, height: 16 }}
                            />
                          ) : (
                            <span style={{ fontSize: '16px' }}>🎨</span>
                          )}
                        </IconButton>
                      )}
                      {isSpacingField(field.field) && (
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleSpacingMenuOpen(
                              event,
                              rowIndex,
                              colIndex,
                              field.field
                            )
                          }
                          sx={{ mr: 1 }}
                          title={`Spacing: ${getSpacingValue(
                            field.field,
                            field.spacing
                          )}px`}
                        >
                          <span style={{ fontSize: '16px' }}>📏</span>
                        </IconButton>
                      )}
                      {!isSpacingField(field.field) && (
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleCustomLabelMenuOpen(
                              event,
                              rowIndex,
                              colIndex,
                              field.field
                            )
                          }
                          sx={{ mr: 1 }}
                          title={
                            field.customLabel
                              ? `Custom Label: ${field.customLabel}`
                              : 'Add Custom Label'
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!isSpacingField(field.field) && (
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleFontMenuOpen(
                              event,
                              rowIndex,
                              colIndex,
                              field.field
                            )
                          }
                          sx={{ mr: 1 }}
                          title={
                            field.font
                              ? `Font Size: ${field.font.size || 'default'}px`
                              : 'Configure Font Size'
                          }
                        >
                          <span style={{ fontSize: '16px' }}>🔤</span>
                        </IconButton>
                      )}
                      {!isSpacingField(field.field) &&
                        supportsAvatar(field.field) && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              toggleFieldAvatar(rowIndex, colIndex, field.field)
                            }
                            sx={{ mr: 1 }}
                            title={
                              field.showAvatar ? 'Hide Avatar' : 'Show Avatar'
                            }
                            color={field.showAvatar ? 'primary' : 'default'}
                          >
                            <AccountCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      <IconButton
                        size="small"
                        onClick={() =>
                          removeField(rowIndex, colIndex, field.field)
                        }
                        title="Remove field"
                        sx={{ color: '#666' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Draggable>
              ))}
            {provided.placeholder}
            {column.fields.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                Drop fields here
              </Typography>
            )}
          </Box>
        )}
      </StrictModeDroppable>
    </Box>
  );

  const renderRow = (row, rowIndex) => (
    <Card key={row.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            Row {rowIndex + 1} ({row.columns.length} column
            {row.columns.length !== 1 ? 's' : ''})
          </Typography>
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => addColumn(rowIndex)}
              disabled={row.columns.length >= 3}
              sx={{ mr: 1 }}
            >
              + Add Column
            </Button>
            <Button
              size="small"
              onClick={() => removeRow(rowIndex)}
              color="error"
              disabled={config.layout.rows.length <= 1}
            >
              ✕ Remove Row
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            minHeight: 120,
          }}
        >
          {row.columns.map((column, colIndex) =>
            renderColumn(rowIndex, colIndex, column)
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const getPreviewLabel = (fieldName) => {
    const labels = {
      name: 'Name: ',
      priority: 'Priority: ',
      phone: 'Phone: ',
      email: 'Email: ',
      setter: 'Setter: ',
      closer: 'Closer: ',
      followUpDate: 'Follow-up: ',
      dealType: 'Type: ',
      description: 'Notes: ',
      company: 'Company: ',
      location: 'Location: ',
    };
    return labels[fieldName] || `${fieldName}: `;
  };

  const getPreviewValue = (fieldName, showLabel = false, customLabel = '') => {
    const sampleData = {
      name: 'Christopher Aniston',
      priority: 'High',
      phone: '(800) 987-0998',
      email: 'christopher.aniston@gmail.com',
      setter: 'Christopher Aniston',
      closer: 'John Doe',
      followUpDate: '11/03/28',
      dealType: 'Solar Installation',
      description:
        'Highly motivated candidate with excellent track record in B2B sales. Previously exceeded targets...',
      company: 'Solar Panels Corp',
      location: 'Miami, FL',
    };

    const value = sampleData[fieldName] || 'Sample Value';
    let label = '';

    if (showLabel) {
      if (customLabel) {
        label = customLabel.endsWith(': ') ? customLabel : `${customLabel}: `;
      } else {
        label = getPreviewLabel(fieldName);
      }
    }

    return `${label}${value}`;
  };

  const getFieldFontStyle = (field) => {
    if (!field.font || !field.font.size) return {};

    return {
      fontSize: `${field.font.size}px`,
    };
  };

  const renderPreview = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Deal Card Preview
        </Typography>
        <Box
          sx={{
            width: 300,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 2,
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative',
          }}
        >
          {/* Preview Dropdown Menu */}
          {config.dropdown?.enabled && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0',
                fontSize: '12px',
              }}
              title="Action Menu (⋮)"
            >
              ⋮
            </Box>
          )}
          {config.layout.rows &&
            config.layout.rows.map((row, rowIndex) => (
              <Box
                key={row.id}
                sx={{ mb: rowIndex < config.layout.rows.length - 1 ? 2 : 0 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                  }}
                >
                  {row.columns.map((column, colIndex) => (
                    <Box
                      key={`${rowIndex}-${colIndex}`}
                      sx={{
                        flex:
                          column.width === '100%'
                            ? 1
                            : column.width === '70%'
                            ? 0.7
                            : column.width === '50%'
                            ? 0.5
                            : column.width === '33.33%'
                            ? 0.33
                            : column.width === '30%'
                            ? 0.3
                            : 1,
                        minWidth: 0,
                      }}
                    >
                      {column.fields
                        .filter((field) => field.enabled)
                        .sort((a, b) => a.position - b.position)
                        .map((field, fieldIndex) => (
                          <Box
                            key={field.field}
                            sx={{
                              mb:
                                fieldIndex <
                                column.fields.filter((f) => f.enabled).length -
                                  1
                                  ? 0.5
                                  : 0,
                            }}
                          >
                            {field.field === 'name' ? (
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={getFieldFontStyle(field)}
                              >
                                {getPreviewValue(
                                  field.field,
                                  field.showLabel,
                                  field.customLabel
                                )}
                              </Typography>
                            ) : field.field === 'priority' ? (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: '#f44336',
                                  color: 'white',
                                  borderRadius: 1,
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  ...getFieldFontStyle(field),
                                }}
                              >
                                {getPreviewValue(
                                  field.field,
                                  field.showLabel,
                                  field.customLabel
                                )}
                              </Box>
                            ) : field.field === 'dealType' ? (
                              <Chip
                                label={getPreviewValue(
                                  field.field,
                                  field.showLabel,
                                  field.customLabel
                                )}
                                size="small"
                                sx={{
                                  fontSize: '11px',
                                  '& .MuiChip-label': getFieldFontStyle(field),
                                }}
                              />
                            ) : field.field === 'description' ? (
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                  fontSize: '12px',
                                  ...getFieldFontStyle(field),
                                }}
                              >
                                {getPreviewValue(
                                  field.field,
                                  field.showLabel,
                                  field.customLabel
                                )}
                              </Typography>
                            ) : isSpacingField(field.field) ? (
                              <Box
                                sx={{
                                  height: `${getSpacingValue(
                                    field.field,
                                    field.spacing
                                  )}px`,
                                  backgroundColor: '#f0f0f0',
                                  border: '1px dashed #ccc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  color: '#999',
                                }}
                              >
                                {getSpacingValue(field.field, field.spacing)}px
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {field.showAvatar &&
                                supportsAvatar(field.field) ? (
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: '50%',
                                      backgroundColor: '#e0e0e0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '10px',
                                    }}
                                  >
                                    👤
                                  </Box>
                                ) : field.icon ? (
                                  <img
                                    src={
                                      availableIcons.find(
                                        (icon) => icon.name === field.icon
                                      )?.image
                                    }
                                    alt={field.icon}
                                    style={{ width: 14, height: 14 }}
                                  />
                                ) : null}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '13px',
                                    ...getFieldFontStyle(field),
                                  }}
                                >
                                  {getPreviewValue(
                                    field.field,
                                    field.showLabel,
                                    field.customLabel
                                  )}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

          {/* Map Buttons Preview */}
          {config.mapButtons?.enabled && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              {config.mapButtons.showDirections && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: '#e3f2fd',
                    borderRadius: 1,
                    fontSize: '12px',
                    color: '#1976d2',
                    fontWeight: 500,
                  }}
                >
                  🗺️ Directions
                </Box>
              )}
              {config.mapButtons.showOnMap && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    backgroundColor: '#e8f5e8',
                    borderRadius: 1,
                    fontSize: '12px',
                    color: '#2e7d32',
                    fontWeight: 500,
                  }}
                >
                  📍 Show on Map
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Deal Card Configuration
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
          Create flexible deal card layouts with multiple columns per row.
          {/* Perfect for side-by-side information like &ldquo;Name |
          Priority&rdquo; or &ldquo;Company | Location&rdquo;. */}
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #bbdefb',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            💡 How to use:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Drag blue pills</strong> from &ldquo;Available
            Fields&rdquo; to any column
            <br />• <strong>Add columns</strong> to create side-by-side layouts
            (max 3 per row)
            <br />• <strong>Drag between columns</strong> to rearrange fields
            <br />• <strong>Toggle visibility</strong> with the eye icon
            <br />• <strong>Remove fields</strong> by dragging back to
            &ldquo;Available Fields&rdquo;
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Configuration Panel */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h5">Card Layout</Typography>
              <Button
                variant="contained"
                onClick={addRow}
                startIcon={<span>+</span>}
              >
                Add New Row (2 columns)
              </Button>
            </Box>

            {config.layout.rows && config.layout.rows.length > 0 ? (
              config.layout.rows.map((row, rowIndex) =>
                renderRow(row, rowIndex)
              )
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No layout configured yet. Click &ldquo;Add New Row&rdquo; to
                  create your first multi-column layout!
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  💡 Each row starts with 2 columns for side-by-side field
                  arrangements
                </Typography>
              </Box>
            )}

            {/* Available Fields */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Fields
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Drag these fields to any section above to add them to your
                  card layout
                </Typography>
                <StrictModeDroppable droppableId="available">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: 80,
                        p: 2,
                        border: snapshot.isDraggingOver
                          ? '2px dashed #1976d2'
                          : '2px dashed #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: snapshot.isDraggingOver
                          ? '#e3f2fd'
                          : '#fafafa',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'flex-start',
                        alignContent: 'flex-start',
                      }}
                    >
                      {availableFields.map((field, index) => (
                        <Draggable
                          key={field}
                          draggableId={field}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <Chip
                                label={field}
                                size="medium"
                                sx={{
                                  backgroundColor: snapshot.isDragging
                                    ? '#1976d2'
                                    : '#2196f3',
                                  color: snapshot.isDragging
                                    ? 'white'
                                    : 'white',
                                  cursor: 'grab',
                                  '&:hover': {
                                    backgroundColor: '#1565c0',
                                  },
                                  fontWeight: 500,
                                  boxShadow: snapshot.isDragging
                                    ? '0 4px 8px rgba(0,0,0,0.2)'
                                    : '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {availableFields.length === 0 && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            textAlign: 'center',
                            py: 3,
                            width: '100%',
                            fontStyle: 'italic',
                          }}
                        >
                          🎉 All fields are assigned to sections!
                          <br />
                          Drag fields between sections to reorganize your card
                          layout.
                        </Typography>
                      )}
                    </Box>
                  )}
                </StrictModeDroppable>
              </CardContent>
            </Card>

            {/* Customization Options */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customization Options
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  Add spacing and other layout elements to your card
                </Typography>

                {/* Add Spacing Button */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="Spacing (px)"
                    type="number"
                    value={spacingInputValue}
                    onChange={(e) => setSpacingInputValue(e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ min: 1, max: 100 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddSpacing}
                    startIcon={<span>📏</span>}
                  >
                    Add Spacing
                  </Button>
                </Box>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  💡 Spacing adds empty space between elements. Default is 5px.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Preview Panel */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              {renderPreview()}

              {/* Dropdown Configuration */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action Menu Configuration
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Configure the dropdown menu that appears in the top-right
                    corner of each deal card
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant={
                        config.dropdown?.enabled ? 'contained' : 'outlined'
                      }
                      onClick={toggleDropdown}
                      startIcon={
                        config.dropdown?.enabled ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )
                      }
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {config.dropdown?.enabled
                        ? 'Menu Enabled'
                        : 'Menu Disabled'}
                    </Button>
                  </Box>

                  {config.dropdown?.enabled && (
                    <Box sx={{ pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Available Actions:
                      </Typography>

                      {[
                        { key: 'showEdit', label: 'Edit Deal', icon: '✏️' },
                        {
                          key: 'showChangeStage',
                          label: 'Change Stage',
                          icon: '🔄',
                        },
                        {
                          key: 'showSchedule',
                          label: 'Schedule Appointment',
                          icon: '📅',
                        },
                        { key: 'showDelete', label: 'Delete Deal', icon: '🗑️' },
                      ].map((option) => (
                        <Box
                          key={option.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <span style={{ fontSize: '14px' }}>
                              {option.icon}
                            </span>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={
                              config.dropdown?.[option.key]
                                ? 'contained'
                                : 'outlined'
                            }
                            onClick={() => toggleDropdownOption(option.key)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            {config.dropdown?.[option.key] ? 'ON' : 'OFF'}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Map Buttons Configuration */}
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Map Buttons Configuration
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Configure map direction buttons that appear when deals have
                    location data
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant={
                        config.mapButtons?.enabled ? 'contained' : 'outlined'
                      }
                      onClick={toggleMapButtons}
                      startIcon={
                        config.mapButtons?.enabled ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )
                      }
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {config.mapButtons?.enabled
                        ? 'Map Buttons Enabled'
                        : 'Map Buttons Disabled'}
                    </Button>
                  </Box>

                  {config.mapButtons?.enabled && (
                    <Box sx={{ pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Available Buttons:
                      </Typography>

                      {[
                        {
                          key: 'showDirections',
                          label: 'Show Directions',
                          icon: '🗺️',
                        },
                        {
                          key: 'showOnMap',
                          label: 'Show on Paiv Map',
                          icon: '📍',
                        },
                      ].map((option) => (
                        <Box
                          key={option.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <span style={{ fontSize: '14px' }}>
                              {option.icon}
                            </span>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={
                              config.mapButtons?.[option.key]
                                ? 'contained'
                                : 'outlined'
                            }
                            onClick={() => toggleMapButtonOption(option.key)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            {config.mapButtons?.[option.key] ? 'ON' : 'OFF'}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>

        {/* Save Button and Status */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1,
            zIndex: 1000,
          }}
        >
          {/* Save Status */}
          {lastSaved && (
            <Typography
              variant="caption"
              sx={{
                color: hasUnsavedChanges ? '#ff9800' : '#4caf50',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              {hasUnsavedChanges
                ? '● Unsaved changes'
                : `✓ Saved ${lastSaved.toLocaleTimeString()}`}
            </Typography>
          )}

          {/* Save Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            disabled={!hasUnsavedChanges || isSaving}
            onClick={() => handleSave(config)}
            sx={{
              minWidth: 120,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              },
            }}
          >
            {isSaving ? (
              <>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: 1,
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                Saving...
              </>
            ) : hasUnsavedChanges ? (
              'Save Changes'
            ) : (
              'Saved'
            )}
          </Button>
        </Box>

        {/* Icon Selection Menu */}
        <Menu
          anchorEl={iconMenuAnchor}
          open={Boolean(iconMenuAnchor)}
          onClose={handleIconMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: 200,
            },
          }}
        >
          <MenuItem
            onClick={removeIcon}
            disabled={
              !selectedFieldForIcon ||
              !config.layout.rows?.[selectedFieldForIcon?.rowIndex]?.columns?.[
                selectedFieldForIcon?.colIndex
              ]?.fields?.find(
                (f) => f.field === selectedFieldForIcon?.fieldName
              )?.icon
            }
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="red" />
            </ListItemIcon>
            <ListItemText>Remove Icon</ListItemText>
          </MenuItem>
          {availableIcons.map((icon) => (
            <MenuItem
              key={icon.name}
              onClick={() => handleIconSelect(icon.name)}
            >
              <ListItemIcon>
                <img
                  src={icon.image}
                  alt={icon.label}
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              <ListItemText>{icon.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        {/* Spacing Configuration Menu */}
        <Menu
          anchorEl={spacingMenuAnchor}
          open={Boolean(spacingMenuAnchor)}
          onClose={handleSpacingMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: 180,
            },
          }}
        >
          <MenuItem onClick={removeSpacing}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove Spacing</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSpacingSelect(5)}>
            <ListItemText>5px (Small)</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSpacingSelect(10)}>
            <ListItemText>10px (Medium)</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSpacingSelect(15)}>
            <ListItemText>15px</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSpacingSelect(20)}>
            <ListItemText>20px (Large)</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleSpacingSelect(30)}>
            <ListItemText>30px (Extra Large)</ListItemText>
          </MenuItem>
        </Menu>

        {/* Custom Label Dialog */}
        <Dialog
          open={Boolean(customLabelMenuAnchor)}
          onClose={handleCustomLabelMenuClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Custom Label</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Custom Label"
              fullWidth
              variant="outlined"
              value={customLabelInput}
              onChange={(e) => setCustomLabelInput(e.target.value)}
              placeholder="Enter custom label (e.g., 'Sales Rep:', 'Account Manager:')"
              helperText="Leave empty to use default label"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={removeCustomLabel} color="error">
              Remove Label
            </Button>
            <Button onClick={handleCustomLabelMenuClose}>Cancel</Button>
            <Button onClick={handleCustomLabelSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Font Configuration Dialog */}
        <Dialog
          open={Boolean(fontMenuAnchor)}
          onClose={handleFontMenuClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Font Size Configuration</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}
            >
              {/* Font Size */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Font Size
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[8, 10, 12, 14, 16, 18, 20].map((size) => (
                    <Button
                      key={size}
                      size="small"
                      variant={
                        selectedFieldForFont &&
                        config.layout.rows?.[
                          selectedFieldForFont?.rowIndex
                        ]?.columns?.[
                          selectedFieldForFont?.colIndex
                        ]?.fields?.find(
                          (f) => f.field === selectedFieldForFont?.fieldName
                        )?.font?.size === size
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => updateFieldFont(size)}
                      sx={{ minWidth: 'auto', px: 2 }}
                    >
                      {size}px
                    </Button>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetFieldFont} color="error">
              Reset to Default
            </Button>
            <Button onClick={handleFontMenuClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DragDropContext>
  );
};

export default DealCardEditor;
